import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  signal,
  computed,
  HostListener,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, ObservedValueOf, Subject, of, takeUntil } from 'rxjs';
import { catchError, filter, skipWhile, take } from 'rxjs/operators';

import { environment } from '@ups/xplat/core';
import {
  SecurityService,
  ViewFeatureExpression,
  isPermissionsSet,
} from '@ups/security';

import { AskRynComponent } from '../ask-ryn/ask-ryn.component';
import { AskRynUiService } from '../../services/ask-ryn-ui.service';

/**
 * The signed-in user's security profile for conversation scoping, or null when it cannot be read. The profile is
 * optional here, and the launcher is mounted in every app on the shared layout: a failed lookup (a 401 in an app
 * with no Software Security sign-in, such as Total RynCall, or a 5xx) leaves the ids blank instead of surfacing as
 * an uncaught page error.
 */
export type AskRynSecurityProfile = ObservedValueOf<
  ReturnType<SecurityService['getSecurityProfileOfLoggedInUser']>
>;

export function optionalSecurityProfile(
  security: Pick<SecurityService, 'getSecurityProfileOfLoggedInUser'>
): Observable<AskRynSecurityProfile> {
  return security
    .getSecurityProfileOfLoggedInUser()
    .pipe(catchError(() => of(null)));
}

/**
 * Pure decision for whether the docked Ask Ryn launcher should be exposed to
 * the current user. Fail CLOSED: the host is mounted app-wide in the shared
 * layout, so an app that names no gating feature must NOT default to visible
 * for everyone — it must explicitly opt in by setting `environment.askRyn
 * .featureUniqName`. Only when a feature is named AND the user is granted it
 * (ViewFeature) does the launcher appear.
 *
 * Regression guard for the 2026-06-16 prod incident: web-warehouse inherited
 * the shared-layout launcher but set no featureUniqName, and the prior
 * fail-OPEN default (`if (!featureName) canUse = true`) exposed the in-dev
 * Ask Ryn button to all users.
 */
export function shouldExposeAskRyn(
  featureName: string | null | undefined,
  isFeatureGranted: () => boolean
): boolean {
  if (!featureName) {
    return false;
  }
  return isFeatureGranted();
}

/**
 * App-wide Ask Ryn surface: a docked launcher (bottom-right) that opens the Ask
 * Ryn chat panel. Mounted once in the shared layout alongside the walkthrough
 * player so every page gets it. Open/close lives in AskRynUiService so the
 * docked Ryn/wren avatar (in-app-help) can open the chat too. Gathers the chat
 * context (logged-in userId, appId, appName, current route) so the host doesn't
 * have to be threaded through every page.
 */
@Component({
  selector: 'ups-ask-ryn-host',
  standalone: true,
  imports: [CommonModule, AskRynComponent],
  templateUrl: './ask-ryn-host.component.html',
  styleUrls: ['./ask-ryn-host.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AskRynHostComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  readonly userId = signal('');
  // The logged-in user's display name, forwarded to the bridge so the Ask Ryn
  // -> Teams mirror shows who asked. Empty until the profile resolves (and when
  // the profile has no name), in which case the bridge falls back to a generic
  // label.
  readonly userDisplayName = signal('');
  readonly appId = environment.security?.appId ?? '';
  readonly appName = environment.sidebarName ?? environment.name ?? '';
  /** router.url as a signal: OnPush does not re-read a plain getter on navigation. */
  readonly currentUrl = signal('');

  constructor(
    public readonly ui: AskRynUiService,
    public readonly router: Router,
    private readonly security: SecurityService
  ) {}

  // --- Draggable + resizable panel -------------------------------------------
  // The panel is positioned by explicit top/left + width/height so the user can
  // drag it (by the header) and resize it (top-left handle, the natural corner
  // for a bottom-right-docked panel). Null signals = not yet touched; the
  // computed getters fall back to a bottom-right default derived from the live
  // viewport, so first open still hugs the bottom-right corner.
  private readonly MIN_W = 360;
  private readonly MIN_H = 320;
  private readonly MARGIN = 16;
  private readonly DEFAULT_W = 850;
  private readonly DEFAULT_H = 784;

  private readonly posLeft = signal<number | null>(null);
  private readonly posTop = signal<number | null>(null);
  private readonly sizeW = signal<number | null>(null);
  private readonly sizeH = signal<number | null>(null);

  readonly width = computed(() => this.sizeW() ?? this.defaultW());
  readonly height = computed(() => this.sizeH() ?? this.defaultH());
  readonly left = computed(() => this.posLeft() ?? this.defaultLeft());
  readonly top = computed(() => this.posTop() ?? this.defaultTop());

  private defaultW(): number {
    return Math.min(this.DEFAULT_W, window.innerWidth - this.MARGIN * 2);
  }
  private defaultH(): number {
    return Math.min(this.DEFAULT_H, window.innerHeight - this.MARGIN * 2);
  }
  private defaultLeft(): number {
    return Math.max(
      this.MARGIN,
      window.innerWidth - this.defaultW() - this.MARGIN
    );
  }
  private defaultTop(): number {
    return Math.max(
      this.MARGIN,
      window.innerHeight - this.defaultH() - this.MARGIN
    );
  }

  private drag: {
    mode: 'move' | 'resize';
    x: number;
    y: number;
    left: number;
    top: number;
    w: number;
    h: number;
  } | null = null;

  /** Begin moving the panel (header drag). Ignores the close button. */
  startMove(ev: MouseEvent): void {
    if ((ev.target as HTMLElement).closest('.ask-ryn-panel__close')) return;
    this.beginDrag('move', ev);
  }

  /** Begin resizing from the top-left handle (bottom-right corner stays put). */
  startResize(ev: MouseEvent): void {
    this.beginDrag('resize', ev);
  }

  private beginDrag(mode: 'move' | 'resize', ev: MouseEvent): void {
    if (ev.button !== 0) return;
    this.drag = {
      mode,
      x: ev.clientX,
      y: ev.clientY,
      left: this.left(),
      top: this.top(),
      w: this.width(),
      h: this.height(),
    };
    ev.preventDefault();
  }

  @HostListener('document:mousemove', ['$event'])
  onDragMove(ev: MouseEvent): void {
    const d = this.drag;
    if (!d) return;
    const dx = ev.clientX - d.x;
    const dy = ev.clientY - d.y;
    if (d.mode === 'move') {
      const maxLeft = Math.max(
        this.MARGIN,
        window.innerWidth - this.width() - this.MARGIN
      );
      const maxTop = Math.max(
        this.MARGIN,
        window.innerHeight - this.height() - this.MARGIN
      );
      this.posLeft.set(Math.min(Math.max(this.MARGIN, d.left + dx), maxLeft));
      this.posTop.set(Math.min(Math.max(this.MARGIN, d.top + dy), maxTop));
    } else {
      // Top-left handle: keep the bottom-right corner anchored, grow up-left.
      const right = d.left + d.w;
      const bottom = d.top + d.h;
      const newLeft = Math.max(
        this.MARGIN,
        Math.min(d.left + dx, right - this.MIN_W)
      );
      const newTop = Math.max(
        this.MARGIN,
        Math.min(d.top + dy, bottom - this.MIN_H)
      );
      this.posLeft.set(newLeft);
      this.posTop.set(newTop);
      this.sizeW.set(right - newLeft);
      this.sizeH.set(bottom - newTop);
    }
  }

  @HostListener('document:mouseup')
  onDragEnd(): void {
    this.drag = null;
  }

  ngOnInit(): void {
    this.currentUrl.set(this.router.url);
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => this.currentUrl.set(this.router.url));

    // Resolve the logged-in user's security UserId for conversation scoping,
    // and their display name for the Ask Ryn -> Teams mirror.
    optionalSecurityProfile(this.security)
      .pipe(takeUntil(this.destroy$))
      .subscribe((profile) => {
        this.userId.set(profile?.userId ?? '');
        this.userDisplayName.set(
          [profile?.firstName, profile?.lastName]
            .filter((part) => !!part && part.trim())
            .join(' ')
            .trim()
        );
      });

    // Ask Ryn availability gate. The launcher is mounted app-wide in the shared
    // layout, so it is exposed ONLY when this app explicitly names a gating
    // feature AND the user is granted ViewFeature on it. Fail CLOSED: an app
    // that names no feature gets no launcher (canUse starts false and stays
    // false), so a feature-less app never exposes the in-dev surface to all
    // users. See shouldExposeAskRyn for the decision + incident context.
    const featureName = environment.askRyn?.featureUniqName;
    if (!featureName) {
      this.ui.canUse.set(shouldExposeAskRyn(featureName, () => false));
      return;
    }
    this.security.permissionsChanged$
      .pipe(
        skipWhile((e) => !isPermissionsSet(e)),
        take(1),
        takeUntil(this.destroy$)
      )
      .subscribe(() =>
        this.ui.canUse.set(
          shouldExposeAskRyn(featureName, () =>
            ViewFeatureExpression.evaluate(featureName, this.security)
          )
        )
      );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
